import { FC, useEffect } from 'react';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	FormGroup,
	FormLabel,
	MenuItem,
	Switch,
	TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
	createPgPayabilityRuleRequest,
	resetPgSaveSuccess,
	updatePgPayabilityRuleRequest,
} from 'redux/reducers/pgSettings/reducer';
import { selectPgSaveLoading, selectPgSaveSuccess } from 'redux/reducers/pgSettings/selectors';
import {
	TPgPayabilityRule,
	TPgPayabilityRuleRequest,
	TPgService,
} from 'services/api/paymentGateway/types';
import { PG_CHANNEL_LABELS } from '../../constants';

type TPayabilityRuleDialogProps = {
	open: boolean;
	onClose: () => void;
	ruleToEdit: TPgPayabilityRule | null;
	services: TPgService[];
};

type TRuleFormValues = {
	name: string;
	isActive: boolean;
	priority: string;
	serviceId: number | 'all';
	invoiceIdBelow: string;
	blockedFromDay: string;
	blockedToDay: string;
	requireDueDateInCurrentMonth: boolean;
	message: string;
	applyLink: boolean;
	applyDashboard: boolean;
	applyAuto: boolean;
	applyAdmin: boolean;
};

/** The rule of decision 12, offered for a new rule. */
const NEW_RULE_VALUES: TRuleFormValues = {
	name: 'Debt transferred to the next invoice',
	isActive: true,
	priority: '10',
	serviceId: 'all',
	invoiceIdBelow: '1000000',
	blockedFromDay: '1',
	blockedToDay: '4',
	requireDueDateInCurrentMonth: true,
	message: 'outstanding balance transferred to the subsequent invoice',
	applyLink: true,
	applyDashboard: true,
	applyAuto: true,
	applyAdmin: false,
};

const toValues = (rule: TPgPayabilityRule): TRuleFormValues => ({
	name: rule.name,
	isActive: rule.isActive,
	priority: String(rule.priority),
	serviceId: rule.serviceId ?? 'all',
	invoiceIdBelow: rule.invoiceIdBelow === null ? '' : String(rule.invoiceIdBelow),
	blockedFromDay: rule.blockedFromDay === null ? '' : String(rule.blockedFromDay),
	blockedToDay: rule.blockedToDay === null ? '' : String(rule.blockedToDay),
	requireDueDateInCurrentMonth: rule.requireDueDateInCurrentMonth,
	message: rule.message,
	applyLink: rule.applyLink,
	applyDashboard: rule.applyDashboard,
	applyAuto: rule.applyAuto,
	applyAdmin: rule.applyAdmin,
});

const day = Yup.number()
	.transform((value, original) => (original === '' ? undefined : value))
	.typeError('A day 1–31')
	.integer('A day 1–31')
	.min(1, 'A day 1–31')
	.max(31, 'A day 1–31');

const validationSchema = Yup.object({
	name: Yup.string().trim().required('Name is required').max(200, 'Max 200 characters'),
	priority: Yup.number()
		.typeError('Must be a whole number')
		.integer('Must be a whole number')
		.required('Priority is required'),
	invoiceIdBelow: Yup.number()
		.transform((value, original) => (original === '' ? undefined : value))
		.typeError('Must be a positive whole number')
		.integer('Must be a positive whole number')
		.positive('Must be a positive whole number'),
	blockedFromDay: day.test('pair', 'Set both days or neither', function pair(value) {
		return (value === undefined) === (this.parent.blockedToDay === '');
	}),
	blockedToDay: day
		.test('pair', 'Set both days or neither', function pair(value) {
			return (value === undefined) === (this.parent.blockedFromDay === '');
		})
		.test('order', 'Must be on or after the first blocked day', function order(value) {
			const from = Number(this.parent.blockedFromDay);
			return value === undefined || this.parent.blockedFromDay === '' || value >= from;
		}),
	message: Yup.string().trim().required('Message is required').max(1000, 'Max 1000 characters'),
});

const optionalInt = (value: string): number | null => (value.trim() === '' ? null : Number(value));

/** Create / edit a debt transfer (payability) rule (03 §4.2, 04 §0.3). */
export const PayabilityRuleDialog: FC<TPayabilityRuleDialogProps> = ({
	open,
	onClose,
	ruleToEdit,
	services,
}) => {
	const dispatch = useDispatch();
	const isEdit = !!ruleToEdit;
	const loading = useSelector(selectPgSaveLoading('payabilityRule'));
	const success = useSelector(selectPgSaveSuccess('payabilityRule'));

	const formik = useFormik<TRuleFormValues>({
		initialValues: ruleToEdit ? toValues(ruleToEdit) : NEW_RULE_VALUES,
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			const body: TPgPayabilityRuleRequest = {
				name: values.name.trim(),
				isActive: values.isActive,
				priority: Number(values.priority),
				serviceId: values.serviceId === 'all' ? null : values.serviceId,
				invoiceIdBelow: optionalInt(values.invoiceIdBelow),
				blockedFromDay: optionalInt(values.blockedFromDay),
				blockedToDay: optionalInt(values.blockedToDay),
				requireDueDateInCurrentMonth: values.requireDueDateInCurrentMonth,
				message: values.message.trim(),
				applyLink: values.applyLink,
				applyDashboard: values.applyDashboard,
				applyAuto: values.applyAuto,
				applyAdmin: values.applyAdmin,
			};
			if (isEdit && ruleToEdit) {
				dispatch(updatePgPayabilityRuleRequest({ id: ruleToEdit.id, body }));
			} else {
				dispatch(createPgPayabilityRuleRequest(body));
			}
		},
	});

	useEffect(() => {
		if (!success) return;
		dispatch(resetPgSaveSuccess('payabilityRule'));
		formik.resetForm();
		onClose();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [success]);

	const handleClose = () => {
		if (loading) return;
		formik.resetForm();
		onClose();
	};

	const numberField = (name: keyof TRuleFormValues, label: string, helperText?: string) => (
		<TextField
			label={label}
			name={name}
			value={formik.values[name]}
			onChange={formik.handleChange}
			onBlur={formik.handleBlur}
			error={formik.touched[name] && !!formik.errors[name]}
			helperText={(formik.touched[name] && formik.errors[name]) || helperText}
			size="small"
			inputProps={{ inputMode: 'numeric' }}
			sx={{ flex: 1, minWidth: 140 }}
		/>
	);

	const channelCheckbox = (
		name: 'applyLink' | 'applyDashboard' | 'applyAuto' | 'applyAdmin',
		label: string,
	) => (
		<FormControlLabel
			control={
				<Checkbox
					checked={formik.values[name]}
					onChange={(e) => {
						void formik.setFieldValue(name, e.target.checked);
					}}
				/>
			}
			label={label}
		/>
	);

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
			<DialogTitle color="primary">
				{isEdit ? `Edit rule #${ruleToEdit?.id}` : 'Add debt transfer rule'}
			</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
						<TextField
							label="Name"
							name="name"
							value={formik.values.name}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.name && !!formik.errors.name}
							helperText={formik.touched.name && formik.errors.name}
							size="small"
							sx={{ flex: 3, minWidth: 240 }}
						/>
						{numberField('priority', 'Priority', 'Lower is checked first')}
					</Box>
					<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
						<TextField
							select
							label="Service"
							name="serviceId"
							value={formik.values.serviceId}
							onChange={(event) => {
								const { value } = event.target;
								void formik.setFieldValue('serviceId', value === 'all' ? 'all' : Number(value));
							}}
							size="small"
							sx={{ flex: 2, minWidth: 220 }}
						>
							<MenuItem value="all">All services</MenuItem>
							{services.map((service) => (
								<MenuItem key={service.id} value={service.id}>
									{service.name} ({service.code})
								</MenuItem>
							))}
						</TextField>
						{numberField(
							'invoiceIdBelow',
							'Only invoices with id below',
							'EAS InvoiceInternalId; empty — all invoices',
						)}
					</Box>
					<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
						{numberField('blockedFromDay', 'Blocked from day', 'Day of month, UTC')}
						{numberField('blockedToDay', 'Blocked to day', 'Inclusive; empty both — no day block')}
					</Box>
					<FormControlLabel
						control={
							<Switch
								checked={formik.values.requireDueDateInCurrentMonth}
								onChange={(e) => {
									void formik.setFieldValue('requireDueDateInCurrentMonth', e.target.checked);
								}}
							/>
						}
						label="Block when the due date is empty or not in the current month (UTC)"
					/>
					<TextField
						label="Message to the customer"
						name="message"
						value={formik.values.message}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.message && !!formik.errors.message}
						helperText={
							(formik.touched.message && formik.errors.message) ||
							'Shown on the payment page and in the dashboard when payment is refused'
						}
						size="small"
						multiline
						minRows={2}
					/>
					<Box>
						<FormLabel sx={{ fontSize: '0.85rem' }}>Applies to</FormLabel>
						<FormGroup row>
							{channelCheckbox('applyLink', PG_CHANNEL_LABELS.link)}
							{channelCheckbox('applyDashboard', PG_CHANNEL_LABELS.dashboard)}
							{channelCheckbox('applyAuto', PG_CHANNEL_LABELS.auto)}
							{channelCheckbox('applyAdmin', PG_CHANNEL_LABELS.admin)}
						</FormGroup>
					</Box>
					<FormControlLabel
						control={
							<Switch
								checked={formik.values.isActive}
								onChange={(e) => {
									void formik.setFieldValue('isActive', e.target.checked);
								}}
							/>
						}
						label="Active"
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					{isEdit ? 'Save' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
